import React from 'react'
import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { useAccountFollowListData } from '@core/Areas/FollowMe/hooks/useAccountFollowListData'
import { PullToRefreshContainer } from '@mobi/component-library/Common/PullToRefreshContainer'
import { SkeletonLoader } from '@core/Areas/FollowMe/Components/Common/SkeletonLoader'
import { FollowProfileItem } from '@core/Areas/FollowMe/Components/ProfileFollowList/Components/FollowProfileItem'
import { EmptyMessage } from '@core/Areas/FollowMe/Components/Common/EmptyMessage'
import { followMePrincipleProfileQuery } from '@core/Areas/FollowMe/helpers/queries'
import type { FollowListTabNav } from '@core/Areas/FollowMe/types'

export const FollowList: React.FC<{
  currentList: FollowListTabNav
  profileId: string | undefined
}> = ({ currentList, profileId }) => {
  const { data: profile } = useQuery(followMePrincipleProfileQuery)

  const isMyProfile = profile?.id === profileId

  const { isLoading, isEmptyState, data, refetch, LoadMoreButton } = useAccountFollowListData(
    currentList,
    profileId
  )

  if (isEmptyState) {
    if (isMyProfile) {
      return currentList === 'followers' ? (
        <EmptyMessage
          title='Share your punting powers'
          description={`Share your bets and watch\n your follower count grow!`}
          messageType='profile'
        />
      ) : (
        <EmptyMessage
          title='No Followed Profiles'
          description={`Follow profiles to\n personalise your feed`}
          messageType='profile'
        />
      )
    }

    return currentList === 'followers' ? (
      <EmptyMessage
        title='No Followers Yet'
        description={'This profile does not have\n any followers yet'}
        messageType='profile'
      />
    ) : (
      <EmptyMessage
        title='No Followed Profiles'
        description={`This profile doesn't follow\n anyone yet`}
        messageType='profile'
      />
    )
  }

  return (
    <PullToRefreshContainer onRefresh={refetch}>
      <FeedContainerStyled>
        {!data || isLoading ? (
          <SkeletonLoader showOnlyHeader={true} />
        ) : (
          <>
            {data.pages.map(feedPage =>
              feedPage.map(profile => (
                <FollowProfileItem key={profile.id} followMeProfile={profile} />
              ))
            )}
          </>
        )}
        <LoadMoreButton />
      </FeedContainerStyled>
    </PullToRefreshContainer>
  )
}

const FeedContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
})
